import { Link, useNavigate } from "react-router-dom";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { BatchTablePayslipType, PayslipType } from "types/payroll-types";
import TableHeader from "components/TableHeader";
import TableCell from "components/TableCell";
import Table from "components/Table";
import { formatAmount } from "utils/utilityFunctions";
import Checkbox from "components/Checkbox";
import { useState } from "react";
import { toast } from "react-toastify";
import { payslipsStatusUpdateAPI } from "api/apiHelper";
import Tab from "components/Tab";


interface PayslipBatchDataTableType {
  tableData: BatchTablePayslipType[];
  tableName: string;
  batchId: number;
  getPaySlipBatch: () => void;
}

function PayslipBatchDataTable(props: PayslipBatchDataTableType) {
  const { tableData, tableName, batchId, getPaySlipBatch } = props;
  const navigate = useNavigate();
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string>("PAID")
  const [selectAll, setSelectAll] = useState(false);

  const handleRowClick = (payslip: BatchTablePayslipType) => {
    navigate(`/payslip/batch/${batchId}/${payslip.id}`);
  };

  async function handlePayslipsStatusChange () {
    const notification = toast.loading("Creating Payslip Batch...");
    try {
      const response = await payslipsStatusUpdateAPI(selectedRows, selectedStatus);
      console.log("Generate Payslips Response: ", response.data);
      getPaySlipBatch();
      toast.update(notification, {
        render: "Payslips status changed successfully",
        type: "success",
        isLoading: false,
        autoClose: 5000,
      });
      // Update the table data to reflect the changes

    } catch (error) {
      toast.update(notification, {
        render: "Failed to update s.",
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
      console.log(error);
    }
  }

  const handleCheckboxChange = (row: any) => {
    const isSelected:Boolean = selectedRows.includes(row.original.id);

    if (isSelected) {
      setSelectedRows((prevSelectedRows) =>
        prevSelectedRows.filter((id:number) => id !== row.original.id)
      );
    } else {
      setSelectedRows((prevSelectedRows:number[]) => [...prevSelectedRows, row.original.id]);
    }
  };
  
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRows([]);
    } else {
      const allIds:number[] = [];
      tableData.forEach((row:BatchTablePayslipType) => {
        allIds.push(row.id);
      });
      setSelectedRows(allIds);
    }
    setSelectAll(!selectAll);
  };
  
  const columnHelper = createColumnHelper<BatchTablePayslipType>();
  const columns:ColumnDef<BatchTablePayslipType, any>[] = [
    columnHelper.accessor("id", {
      id: "id",
      header: () => (
        <TableHeader>ID</TableHeader>
      ),
      cell: (info) => (
        <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
          <Checkbox
             checked={selectedRows.includes(info.row.original.id)}
             onChange={() => handleCheckboxChange(info.row)}
            me="10px"
          />
          <p className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
            <TableCell>{info.getValue()}</TableCell>
          </p>
        </div>
        
      ),
    }),
    columnHelper.accessor("name", {
      id: "name",
      header: () => (
        <TableHeader>Name</TableHeader>
      ),
      cell: (info) => (
        <TableCell>
          {info.getValue()}
        </TableCell>
      ),
    }),
    columnHelper.accessor("total_income", {
      id: "total_income",
      header: () => (
        <TableHeader>Total Income</TableHeader>
      ),
      cell: (info) => (
        <TableCell>
          {formatAmount(info.getValue())}
        </TableCell>
      ),
    }),
    columnHelper.accessor("net_salary", {
      id: "net_salary",
      header: () => (
        <TableHeader>Net Salary</TableHeader>
      ),
      cell: (info) => (
        <TableCell>
          {formatAmount(info.getValue())}
        </TableCell>
      ),
    }),
    columnHelper.accessor("status", {
      id: "status",
      header: () => (
        <TableHeader>Status</TableHeader>
      ),
      cell: (info) => (
        <TableCell>
          {info.getValue()}
        </TableCell>
      ),
    }),
    
  ];
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-end">

        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={handleSelectAll}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              selectAll
                ? "bg-blue-100 text-blue-700 border border-blue-300 hover:bg-blue-200"
                : "bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200"
            }`}
          >
            {!selectAll ? (
              <span className="flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                </svg>
                Select All
              </span>
            ) : (
              <span className="flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Deselect All
              </span>
            )}
          </button>

          <div className="flex items-center">
            <div className="relative inline-block">
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="appearance-none bg-white border border-gray-300 rounded-l-md py-2 pl-3 pr-8 text-sm leading-tight focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                
              >
                <option value="PAID">PAID</option>
                <option value="DRAFT">DRAFT</option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700"></div>
            </div>
            <button
              onClick={handlePayslipsStatusChange}
              disabled={selectedRows.length === 0}
              className={`px-4 py-2 rounded-r-md text-sm font-medium ${
                selectedRows.length > 0
                  ? "bg-brand-500 flex gap-2 justify-between items-center  hover:bg-brand-600 dark:bg-brand-400 dark:hover:bg-brand-500 p-3 text-[1rem] font-bold text-white mb-2 md:mb-0"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
            >
              Go
            </button>
          </div>

          {selectedRows.length > 0 && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {selectedRows.length} selected
            </span>
          )}
        </div>
      </div>
    <div>
      <Table<BatchTablePayslipType> 
        tableData={tableData} 
        columns={columns} 
        tableHeading={tableName}
        onRowClick={handleRowClick}
      />
    </div>
  </div>
  )
  
}

export default PayslipBatchDataTable;